import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Footprints, Radio, CalendarPlus, Loader2, RefreshCw, Route, Timer, Trash2, Users, Car } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { adminGet, adminPost, adminDelete, reportError, formatDate } from './adminApi';
import { RouteMap, formatDistance, formatDuration } from '../easer/RouteMap';
import { toISODate, fmtTime } from '../../lib/easerApi';

const LIVE_REFRESH_MS = 10000;
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return toISODate(d); };

/** Admin tab: live flyer distributions, history map (one colour per Easer) and planning. */
/** Trajet d'accompagnement (bouton ACC d'une prestation) : badge avec les km facturés sur le bon d'intervention (PAFK). */
function AccBadge({ s }) {
  const km = s.km_billed != null ? String(s.km_billed).replace('.', ',') : null;
  return (
    <Badge className="mr-1 bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/15 border border-[hsl(var(--accent))]/30"
      title={km != null ? `${km} km facturés (PAFK)${s.fichinter_ref ? ` · ${s.fichinter_ref}` : ''}` : 'Accompagnement en cours'} data-testid="tract-kind-acc">
      <Car className="h-3 w-3 mr-1" /> ACC{km != null ? ` ${km} km` : ''}
    </Badge>
  );
}


export function AdminTractsTab({ adminKey }) {
  const [live, setLive] = useState(null);
  const [data, setData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [range, setRange] = useState({ start: daysAgo(30), end: toISODate(new Date()) });
  const [userFilter, setUserFilter] = useState('all');
  const [focus, setFocus] = useState(null);
  const [plan, setPlan] = useState({ user_id: '', date: toISODate(new Date()), start_time: '10:00', end_time: '12:00', zone: '' });
  const [planning, setPlanning] = useState(false);

  const loadLive = useCallback(async () => {
    try {
      const res = await adminGet('/api/admin/tracts/live', adminKey);
      setLive(res.data.items);
    } catch (err) {
      reportError('tracts live', err);
      setLive((l) => l || []);
    }
  }, [adminKey]);

  const loadHistory = useCallback(async () => {
    try {
      const params = { start: range.start, end: range.end };
      if (userFilter !== 'all') params.user_id = userFilter;
      const res = await adminGet('/api/admin/tracts', adminKey, params);
      setData(res.data);
    } catch (err) {
      reportError('tracts historique', err, () => toast.error("Impossible de charger l'historique"));
      setData((d) => d || { items: [], easers: [] });
    }
  }, [adminKey, range.start, range.end, userFilter]);

  useEffect(() => {
    adminGet('/api/admin/easer/overview', adminKey).then((r) => setEmployees(r.data.employees || [])).catch((err) => reportError('employés', err));
  }, [adminKey]);
  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => {
    loadLive();
    const id = setInterval(loadLive, LIVE_REFRESH_MS);
    return () => clearInterval(id);
  }, [loadLive]);

  const historyRoutes = useMemo(() => (data?.items || []).filter((s) => s.status === 'done').map((s) => ({
    id: s.id, points: s.points, color: s.color, label: `${s.user_name} · ${formatDate(s.started_at)} · ${formatDistance(s.distance_m)}`,
  })), [data]);
  const liveRoutes = useMemo(() => (live || []).map((s) => ({ id: s.id, points: s.points, color: s.color, label: `${s.user_name} · ${s.label}`, live: true })), [live]);

  const submitPlan = async (e) => {
    e.preventDefault();
    if (!plan.user_id) return toast.error('Choisissez un Easer');
    setPlanning(true);
    try {
      const res = await adminPost('/api/admin/tracts/plan', adminKey, {}, { ...plan, user_id: Number(plan.user_id) });
      toast.success(`Distribution planifiée pour ${res.data.user_name} le ${formatDate(res.data.start)} (agenda Dolibarr #${res.data.event_id})`);
      setPlan((p) => ({ ...p, zone: '' }));
    } catch (err) {
      reportError('planification tracts', err, (m) => toast.error(m || 'Échec de la planification'));
    } finally {
      setPlanning(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Supprimer la distribution de ${s.user_name} du ${formatDate(s.started_at)} ?`)) return;
    try {
      await adminDelete(`/api/admin/tracts/${s.id}`, adminKey);
      toast.success('Distribution supprimée');
      loadHistory();
    } catch (err) {
      reportError('suppression tract', err, (m) => toast.error(m || 'Échec'));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-tracts-tab">
      {/* Live */}
      <Card className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-['Space_Grotesk'] font-semibold flex items-center gap-2"><Radio className="h-4 w-4 text-[hsl(var(--accent))]" /> Distributions en direct
            {live && live.length > 0 && <Badge className="bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))] gap-1"><span className="h-2 w-2 rounded-full bg-white animate-pulse" /> {live.length} en cours</Badge>}
          </h3>
          <span className="text-xs text-muted-foreground">Actualisation automatique toutes les 10 s</span>
        </div>
        {live === null ? <Skeleton className="h-64" /> : live.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="tracts-live-empty">Aucune distribution en cours pour le moment.</p>
        ) : (
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2"><RouteMap routes={liveRoutes} height={360} /></div>
            <ul className="space-y-2" data-testid="tracts-live-list">
              {live.map((s) => (
                <li key={s.id} className="rounded-lg border border-border p-3 text-sm">
                  <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: s.color }} /><span className="font-semibold">{s.user_name}</span></div>
                  <p className="text-muted-foreground truncate">{s.kind === 'accompagnement' && <AccBadge s={s} />}{s.label}</p>
                  <p className="text-xs text-muted-foreground mt-1">Depuis {fmtTime(s.started_at)} · {formatDistance(s.distance_m)} · {s.point_count} pts{s.last_position ? ` · dernier signal ${fmtTime(s.last_position.ts)}` : ''}</p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      {/* Planification */}
      <Card className="p-5">
        <h3 className="font-['Space_Grotesk'] font-semibold flex items-center gap-2 mb-1"><CalendarPlus className="h-4 w-4" /> Planifier une distribution</h3>
        <p className="text-sm text-muted-foreground mb-4">Crée l'événement dans l'agenda Dolibarr de l'Easer ; il pourra la démarrer depuis son planning. Vous pouvez aussi la créer directement dans Dolibarr avec « tract » ou « distribution » dans le libellé.</p>
        <form onSubmit={submitPlan} className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end" data-testid="tract-plan-form">
          <div className="space-y-1.5 lg:col-span-2">
            <Label>Easer</Label>
            <Select value={plan.user_id} onValueChange={(v) => setPlan((p) => ({ ...p, user_id: v }))}>
              <SelectTrigger data-testid="plan-user"><SelectValue placeholder="Choisir un employé" /></SelectTrigger>
              <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={String(e.id)}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label htmlFor="plan-date">Date</Label><Input id="plan-date" type="date" value={plan.date} onChange={(e) => setPlan((p) => ({ ...p, date: e.target.value }))} data-testid="plan-date" /></div>
          <div className="space-y-1.5"><Label htmlFor="plan-start">De</Label><Input id="plan-start" type="time" value={plan.start_time} onChange={(e) => setPlan((p) => ({ ...p, start_time: e.target.value }))} data-testid="plan-start" /></div>
          <div className="space-y-1.5"><Label htmlFor="plan-end">À</Label><Input id="plan-end" type="time" value={plan.end_time} onChange={(e) => setPlan((p) => ({ ...p, end_time: e.target.value }))} data-testid="plan-end" /></div>
          <div className="space-y-1.5 sm:col-span-2 lg:col-span-5"><Label htmlFor="plan-zone">Secteur / quartier</Label><Input id="plan-zone" value={plan.zone} onChange={(e) => setPlan((p) => ({ ...p, zone: e.target.value }))} placeholder="Ex. Liévin centre, rues autour de la mairie" data-testid="plan-zone" /></div>
          <Button type="submit" disabled={planning} data-testid="plan-submit">{planning ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CalendarPlus className="h-4 w-4 mr-1" />} Planifier</Button>
        </form>
      </Card>

      {/* Historique */}
      <Card className="p-5">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <h3 className="font-['Space_Grotesk'] font-semibold flex items-center gap-2"><Footprints className="h-4 w-4" /> Distributions enregistrées</h3>
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1"><Label htmlFor="hist-start" className="text-xs">Du</Label><Input id="hist-start" type="date" value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} className="h-9" data-testid="hist-start" /></div>
            <div className="space-y-1"><Label htmlFor="hist-end" className="text-xs">Au</Label><Input id="hist-end" type="date" value={range.end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} className="h-9" data-testid="hist-end" /></div>
            <div className="space-y-1 min-w-[180px]"><Label className="text-xs">Easer</Label>
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger className="h-9" data-testid="hist-user"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tous</SelectItem>{employees.map((e) => <SelectItem key={e.id} value={String(e.id)}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button variant="outline" size="sm" className="h-9" onClick={loadHistory} data-testid="hist-refresh"><RefreshCw className="h-4 w-4" /></Button>
          </div>
        </div>

        {data === null ? <Skeleton className="h-80" /> : data.items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="tracts-history-empty">Aucune distribution sur cette période.</p>
        ) : (
          <div className="space-y-4">
            <RouteMap routes={historyRoutes} focusId={focus} height={420} onSelect={setFocus} />
            {/* Légende */}
            <div className="flex flex-wrap gap-2" data-testid="tracts-legend">
              {data.easers.map((e) => (
                <button key={e.user_id} type="button" onClick={() => setUserFilter(userFilter === String(e.user_id) ? 'all' : String(e.user_id))} className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1 text-xs hover:bg-[hsl(var(--secondary))]">
                  <span className="w-3 h-3 rounded-full" style={{ background: e.color }} />
                  <span className="font-medium">{e.user_name}</span>
                  <span className="text-muted-foreground">{e.sessions} · {formatDistance(e.distance_m)} · {formatDuration(e.duration_s)}</span>
                </button>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tracts-table">
                <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="py-2 pr-3 font-medium">Date</th><th className="py-2 pr-3 font-medium">Easer</th><th className="py-2 pr-3 font-medium">Secteur</th><th className="py-2 pr-3 font-medium">Horaires</th><th className="py-2 pr-3 font-medium"><Route className="h-3.5 w-3.5 inline" /> Distance</th><th className="py-2 pr-3 font-medium"><Timer className="h-3.5 w-3.5 inline" /> Durée</th><th className="py-2 pr-3 font-medium">Tracts</th><th className="py-2" /></tr></thead>
                <tbody>
                  {data.items.map((s) => (
                    <tr key={s.id} onClick={() => setFocus(s.id)} className={`border-b border-border/60 cursor-pointer ${focus === s.id ? 'bg-[hsl(var(--secondary))]/70' : 'hover:bg-[hsl(var(--secondary))]/40'}`} data-testid="tract-row">
                      <td className="py-2 pr-3 whitespace-nowrap">{formatDate(s.started_at)}</td>
                      <td className="py-2 pr-3 whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full mr-2 align-middle" style={{ background: s.color }} />{s.user_name}</td>
                      <td className="py-2 pr-3 text-muted-foreground max-w-[220px] truncate">{s.kind === 'accompagnement' && <AccBadge s={s} />}{s.label}</td>
                      <td className="py-2 pr-3 whitespace-nowrap text-muted-foreground">{fmtTime(s.started_at)} → {s.ended_at ? fmtTime(s.ended_at) : <Badge className="bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]">en cours</Badge>}</td>
                      <td className="py-2 pr-3 font-semibold tabular-nums">{formatDistance(s.distance_m)}</td>
                      <td className="py-2 pr-3 tabular-nums">{formatDuration(s.duration_s)}</td>
                      <td className="py-2 pr-3 tabular-nums">{s.flyers ?? '—'}</td>
                      <td className="py-2 text-right"><Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={(e) => { e.stopPropagation(); remove(s); }} aria-label="Supprimer" data-testid="tract-delete"><Trash2 className="h-4 w-4" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Une couleur par Easer. Cliquez sur un tracé ou une ligne pour le mettre en avant, sur un nom de la légende pour filtrer.</p>
          </div>
        )}
      </Card>
    </div>
  );
}
